import React, { useEffect, useState } from "react";
import { cloneDeep, get as lget, set as lset } from "lodash";
import { Plus, Trash2, Save, Loader2, Check, X, Mail, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RichTextEditor from "@/components/admin/RichTextEditor";
import MediaLibrary from "@/components/admin/MediaLibrary";
import ImageField from "@/components/admin/ImageField";
import LivePreview from "@/components/admin/LivePreview";

const SECTIONS = [
  { key: "content", label: "Pages" },
  { key: "media", label: "Media Library" },
  { key: "headfoot", label: "Header & Footer" },
  { key: "settings", label: "Global Settings" },
  { key: "applications", label: "Business applications" },
  { key: "messages", label: "Messages" },
];
const PAGES = [
  { key: "home", label: "Home" },
  { key: "how_it_works", label: "How It Works" },
  { key: "about", label: "About" },
  { key: "faq", label: "FAQ" },
  { key: "contact", label: "Contact" },
];

export default function WebsiteCMS() {
  const [sec, setSec] = useState("content");
  const [page, setPage] = useState("home");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/site").then((r) => setDraft(r.data));
  useEffect(() => { load(); }, []);

  const setPath = (path, val) => { const d = cloneDeep(draft); lset(d, path, val); setDraft(d); };
  const get = (path, fb = "") => lget(draft, path, fb);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/site", draft); setDraft(data); toast.success("Website updated"); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  if (!draft) return <p className="text-[#787672]">Loading…</p>;
  const showSave = !["media", "applications", "messages"].includes(sec);
  const showPreview = ["content", "headfoot", "settings"].includes(sec);

  const editor = (
    <>
      {sec === "content" && <ContentEditor get={get} setPath={setPath} page={page} setPage={setPage} />}
      {sec === "media" && <MediaLibrary />}
      {sec === "headfoot" && <HeadFootEditor get={get} setPath={setPath} draft={draft} setDraft={setDraft} />}
      {sec === "settings" && <SettingsEditor get={get} setPath={setPath} />}
      {sec === "applications" && <Applications />}
      {sec === "messages" && <Messages />}
    </>
  );

  return (
    <div data-testid="website-cms">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="inline-flex flex-wrap rounded-full border border-[#E8E6E1] p-1 bg-white">
          {SECTIONS.map((s) => (
            <button key={s.key} data-testid={`cms-tab-${s.key}`} onClick={() => setSec(s.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${sec === s.key ? "bg-[#2C3531] text-white" : "text-[#787672]"}`}>{s.label}</button>
          ))}
        </div>
        {showSave && (
          <Button data-testid="cms-save-btn" onClick={save} disabled={saving} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422]">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save changes</>}
          </Button>
        )}
      </div>

      {showPreview ? (
        <div className="grid xl:grid-cols-[minmax(0,1fr)_minmax(0,600px)] gap-8 items-start">
          <div>{editor}</div>
          <div className="hidden xl:block">
            <LivePreview draft={draft} page={sec === "content" ? page : "home"} />
          </div>
        </div>
      ) : editor}
    </div>
  );
}

/* ---------- reusable field bits ---------- */
const Field = ({ label, path, get, setPath, testid, placeholder }) => (
  <div><p className="text-sm font-medium text-[#2C3531] mb-1.5">{label}</p>
    <Input data-testid={testid} value={get(path)} onChange={(e) => setPath(path, e.target.value)} placeholder={placeholder} /></div>
);
const Area = ({ label, path, get, setPath, testid, rows = 3 }) => (
  <div><p className="text-sm font-medium text-[#2C3531] mb-1.5">{label}</p>
    <Textarea data-testid={testid} rows={rows} value={get(path)} onChange={(e) => setPath(path, e.target.value)} /></div>
);
const Rich = ({ label, path, get, setPath, testid }) => (
  <div><p className="text-sm font-medium text-[#2C3531] mb-1.5">{label}</p>
    <RichTextEditor value={get(path)} onChange={(v) => setPath(path, v)} testid={testid} /></div>
);

function ListEditor({ label, path, get, setPath, fields, empty }) {
  const items = get(path, []) || [];
  const upd = (arr) => setPath(path, arr);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-[#2C3531]">{label}</p>
        <Button variant="outline" size="sm" className="rounded-full" data-testid={`add-${path}`} onClick={() => upd([...items, cloneDeep(empty)])}><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>
      <div className="space-y-3">
        {items.map((it, i) => (
          <Card key={i} className="p-4 border-[#E8E6E1] shadow-none bg-[#FDFBF7]">
            <div className="flex justify-end mb-1">
              <button onClick={() => upd(items.filter((_, j) => j !== i))} data-testid={`remove-${path}-${i}`} className="text-[#993333]"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="space-y-2.5">
              {fields.map((f) => f.type === "textarea" ? (
                <Textarea key={f.key} rows={2} value={it[f.key] || ""} placeholder={f.label} data-testid={`field-${path}-${i}-${f.key}`}
                  onChange={(e) => { const arr = cloneDeep(items); arr[i][f.key] = e.target.value; upd(arr); }} />
              ) : (
                <Input key={f.key} value={it[f.key] || ""} placeholder={f.label} data-testid={`field-${path}-${i}-${f.key}`}
                  onChange={(e) => { const arr = cloneDeep(items); arr[i][f.key] = e.target.value; upd(arr); }} />
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

const Block = ({ title, children }) => (
  <Card className="p-6 border-[#E8E6E1] shadow-none space-y-4">
    <h3 className="font-serif text-lg font-bold">{title}</h3>{children}
  </Card>
);

/* ---------- page content editor ---------- */
function ContentEditor({ get, setPath, page, setPage }) {
  const P = (p) => `pages.${page}.${p}`;
  return (
    <div className="max-w-3xl space-y-5">
      <div className="w-52">
        <Select value={page} onValueChange={setPage}>
          <SelectTrigger data-testid="cms-page-select"><SelectValue /></SelectTrigger>
          <SelectContent>{PAGES.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {page === "home" && <>
        <Block title="Hero">
          <Field label="Eyebrow" path={P("hero.eyebrow")} {...{ get, setPath }} testid="cms-home-hero-eyebrow" />
          <Area label="Title" path={P("hero.title")} {...{ get, setPath }} testid="cms-home-hero-title" />
          <Area label="Subtitle" path={P("hero.subtitle")} {...{ get, setPath }} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Primary CTA text" path={P("hero.cta_primary")} {...{ get, setPath }} />
            <Field label="Primary CTA link" path={P("hero.cta_primary_href")} {...{ get, setPath }} />
            <Field label="Secondary CTA text" path={P("hero.cta_secondary")} {...{ get, setPath }} />
            <Field label="Secondary CTA link" path={P("hero.cta_secondary_href")} {...{ get, setPath }} />
          </div>
          <ImageField label="Hero image" value={get(P("hero.image"))} onChange={(v) => setPath(P("hero.image"), v)} testid="cms-home-hero-image" />
        </Block>
        <Block title="Problem section">
          <Field label="Eyebrow" path={P("problem.eyebrow")} {...{ get, setPath }} />
          <Field label="Title" path={P("problem.title")} {...{ get, setPath }} />
          <Area label="Intro" path={P("problem.intro")} {...{ get, setPath }} />
          <ListEditor label="Problem points" path={P("problem.items")} {...{ get, setPath }} empty={{ title: "", desc: "" }} fields={[{ key: "title", label: "Title" }, { key: "desc", label: "Description", type: "textarea" }]} />
          <Area label="Punchline" path={P("problem.punchline")} {...{ get, setPath }} />
        </Block>
        <Block title="Solution section">
          <Field label="Eyebrow" path={P("solution.eyebrow")} {...{ get, setPath }} />
          <Field label="Title" path={P("solution.title")} {...{ get, setPath }} />
          <Rich label="Body" path={P("solution.body")} {...{ get, setPath }} testid="cms-solution-body" />
          <Field label="Master calendar label" path={P("solution.master_label")} {...{ get, setPath }} />
          <ListEditor label="Flow nodes" path={P("solution.nodes")} {...{ get, setPath }} empty={{ label: "", desc: "" }} fields={[{ key: "label", label: "Label" }, { key: "desc", label: "Markup note" }]} />
          <StringList label="Steps" path={P("solution.steps")} {...{ get, setPath }} />
        </Block>
        <Block title="Built For section">
          <Field label="Eyebrow" path={P("built_for.eyebrow")} {...{ get, setPath }} />
          <Field label="Title" path={P("built_for.title")} {...{ get, setPath }} />
          <ListEditor label="Specialties" path={P("built_for.items")} {...{ get, setPath }} empty={{ title: "", desc: "" }} fields={[{ key: "title", label: "Title" }, { key: "desc", label: "Description", type: "textarea" }]} />
        </Block>
        <Block title="Features section">
          <Field label="Eyebrow" path={P("features.eyebrow")} {...{ get, setPath }} />
          <Field label="Title" path={P("features.title")} {...{ get, setPath }} />
          <ListEditor label="Features" path={P("features.items")} {...{ get, setPath }} empty={{ title: "", desc: "" }} fields={[{ key: "title", label: "Title" }, { key: "desc", label: "Description", type: "textarea" }]} />
        </Block>
        <Block title="Closing CTA">
          <Field label="Title" path={P("cta.title")} {...{ get, setPath }} />
          <Field label="Subtitle" path={P("cta.subtitle")} {...{ get, setPath }} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Button text" path={P("cta.button")} {...{ get, setPath }} />
            <Field label="Button link" path={P("cta.button_href")} {...{ get, setPath }} />
          </div>
        </Block>
      </>}

      {page === "how_it_works" && <>
        <Block title="Hero">
          <Field label="Eyebrow" path={P("hero.eyebrow")} {...{ get, setPath }} />
          <Field label="Title" path={P("hero.title")} {...{ get, setPath }} />
          <Area label="Subtitle" path={P("hero.subtitle")} {...{ get, setPath }} />
        </Block>
        <Block title="Steps">
          <ListEditor label="Steps" path={P("steps")} {...{ get, setPath }} empty={{ num: "", title: "", desc: "" }} fields={[{ key: "num", label: "Number (e.g. 01)" }, { key: "title", label: "Title" }, { key: "desc", label: "Description", type: "textarea" }]} />
        </Block>
        <Block title="Partner Opportunity Matrix">
          <Field label="Title" path={P("matrix.title")} {...{ get, setPath }} />
          <Field label="Subtitle" path={P("matrix.subtitle")} {...{ get, setPath }} />
          <ListEditor label="Rows" path={P("matrix.rows")} {...{ get, setPath }} empty={{ category: "", footprint: "", value: "", partners: "", markup: "" }}
            fields={[{ key: "category", label: "Category" }, { key: "footprint", label: "Footprint" }, { key: "value", label: "Avg value" }, { key: "partners", label: "Ideal partners" }, { key: "markup", label: "Host markup" }]} />
        </Block>
      </>}

      {page === "about" && <>
        <Block title="Hero"><Field label="Eyebrow" path={P("hero.eyebrow")} {...{ get, setPath }} /><Area label="Title" path={P("hero.title")} {...{ get, setPath }} /></Block>
        <Block title="Mission"><Rich label="Mission statement" path={P("mission.body")} {...{ get, setPath }} testid="cms-mission-body" /></Block>
        <Block title="Power of Synergy">
          <Field label="Title" path={P("synergy.title")} {...{ get, setPath }} />
          <Rich label="Body" path={P("synergy.body")} {...{ get, setPath }} testid="cms-synergy-body" />
          <ImageField label="Image" value={get(P("synergy.image"))} onChange={(v) => setPath(P("synergy.image"), v)} testid="cms-synergy-image" />
          <ListEditor label="Points" path={P("synergy.points")} {...{ get, setPath }} empty={{ label: "" }} fields={[{ key: "label", label: "Point", type: "textarea" }]} />
        </Block>
        <Block title="Why Choose Us comparison">
          <Field label="Title" path={P("why.title")} {...{ get, setPath }} />
          <ListEditor label="Comparison rows" path={P("why.rows")} {...{ get, setPath }} empty={{ ppc: "", whitelabel: "" }} fields={[{ key: "ppc", label: "PPC / SEO route", type: "textarea" }, { key: "whitelabel", label: "Whitelabel route", type: "textarea" }]} />
        </Block>
      </>}

      {page === "faq" && <>
        <Block title="Hero"><Field label="Eyebrow" path={P("hero.eyebrow")} {...{ get, setPath }} /><Field label="Title" path={P("hero.title")} {...{ get, setPath }} /><Area label="Subtitle" path={P("hero.subtitle")} {...{ get, setPath }} /></Block>
        <FaqGroups get={get} setPath={setPath} path={P("groups")} />
      </>}

      {page === "contact" && <>
        <Block title="Hero"><Field label="Eyebrow" path={P("hero.eyebrow")} {...{ get, setPath }} /><Field label="Title" path={P("hero.title")} {...{ get, setPath }} /><Area label="Subtitle" path={P("hero.subtitle")} {...{ get, setPath }} /></Block>
        <Block title="Support channels">
          <Field label="Sales email" path={P("channels.sales_email")} {...{ get, setPath }} />
          <Field label="Support email" path={P("channels.support_email")} {...{ get, setPath }} />
          <Field label="Hours / live chat note" path={P("channels.hours")} {...{ get, setPath }} />
        </Block>
      </>}

      <BlocksEditor get={get} setPath={setPath} path={P("blocks")} />
    </div>
  );
}

function BlocksEditor({ get, setPath, path }) {
  const blocks = get(path, []) || [];
  const upd = (arr) => setPath(path, arr);
  const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now() + Math.random()));
  const add = () => upd([...blocks, { id: uid(), layout: "left", background: "default", eyebrow: "", heading: "", body: "", image: "", cta_label: "", cta_href: "" }]);
  const setField = (i, k, v) => { const arr = cloneDeep(blocks); arr[i][k] = v; upd(arr); };
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= blocks.length) return; const arr = cloneDeep(blocks); [arr[i], arr[j]] = [arr[j], arr[i]]; upd(arr); };

  return (
    <Card className="p-6 border-[#9A8C73]/40 shadow-none bg-[#F7F5F1]" data-testid="blocks-editor">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-serif text-lg font-bold">Custom sections</h3>
        <Button variant="outline" size="sm" className="rounded-full" data-testid="add-block-btn" onClick={add}><Plus className="w-4 h-4 mr-1" /> Add section</Button>
      </div>
      <p className="text-xs text-[#787672] mb-4">Add extra image + text sections to the bottom of this page. Drag order with the arrows.</p>
      {blocks.length === 0 && <p className="text-sm text-[#B0AEA8]">No custom sections yet.</p>}
      <div className="space-y-4">
        {blocks.map((b, i) => (
          <Card key={b.id || i} className="p-4 border-[#E8E6E1] shadow-none bg-white" data-testid={`block-editor-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-[#9A8C73]">Section {i + 1}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`block-up-${i}`} className="w-7 h-7 rounded-md hover:bg-[#F0EFEB] flex items-center justify-center disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => move(i, 1)} disabled={i === blocks.length - 1} data-testid={`block-down-${i}`} className="w-7 h-7 rounded-md hover:bg-[#F0EFEB] flex items-center justify-center disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                <button onClick={() => upd(blocks.filter((_, j) => j !== i))} data-testid={`block-delete-${i}`} className="w-7 h-7 rounded-md hover:bg-[#993333]/10 text-[#993333] flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2.5 mb-2.5">
              <div>
                <p className="text-xs font-medium text-[#787672] mb-1">Image layout</p>
                <Select value={b.layout} onValueChange={(v) => setField(i, "layout", v)}>
                  <SelectTrigger data-testid={`block-layout-${i}`} className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="left">Image left</SelectItem><SelectItem value="right">Image right</SelectItem><SelectItem value="full">Text only / full width</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-xs font-medium text-[#787672] mb-1">Background</p>
                <Select value={b.background} onValueChange={(v) => setField(i, "background", v)}>
                  <SelectTrigger data-testid={`block-bg-${i}`} className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="default">Light</SelectItem><SelectItem value="alt">Warm beige</SelectItem><SelectItem value="dark">Dark green</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2.5">
              <Input value={b.eyebrow || ""} placeholder="Eyebrow (small label)" data-testid={`block-eyebrow-${i}`} onChange={(e) => setField(i, "eyebrow", e.target.value)} />
              <Input value={b.heading || ""} placeholder="Heading" data-testid={`block-heading-${i}`} onChange={(e) => setField(i, "heading", e.target.value)} />
              <RichTextEditor value={b.body} onChange={(v) => setField(i, "body", v)} testid={`block-body-${i}`} />
              <ImageField label="" value={b.image} onChange={(v) => setField(i, "image", v)} testid={`block-image-${i}`} />
              <div className="grid grid-cols-2 gap-2.5">
                <Input value={b.cta_label || ""} placeholder="Button text (optional)" data-testid={`block-cta-label-${i}`} onChange={(e) => setField(i, "cta_label", e.target.value)} />
                <Input value={b.cta_href || ""} placeholder="Button link (e.g. /register)" data-testid={`block-cta-href-${i}`} onChange={(e) => setField(i, "cta_href", e.target.value)} />
              </div>
            </div>
          </Card>
        ))}
      </div>
    </Card>
  );
}

function StringList({ label, path, get, setPath }) {
  const items = get(path, []) || [];
  const upd = (arr) => setPath(path, arr);
  return (
    <div className="space-y-2">
      {items.map((s, i) => (
        <div key={i} className="flex gap-2">
          <Input value={s} data-testid={`string-${path}-${i}`} onChange={(e) => { const arr = [...items]; arr[i] = e.target.value; upd(arr); }} />
          <button onClick={() => upd(items.filter((_, j) => j !== i))} className="text-[#993333] px-2"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <Button variant="outline" size="sm" className="rounded-full" onClick={() => upd([...items, ""])}><Plus className="w-4 h-4 mr-1" /> Add step</Button>
    </div>
  );
}

function FaqGroups({ get, setPath, path }) {
  const groups = get(path, []) || [];
  const upd = (arr) => setPath(path, arr);
  return (
    <div className="space-y-4">
      {groups.map((g, gi) => (
        <Card key={gi} className="p-5 border-[#E8E6E1] shadow-none space-y-3">
          <div className="flex gap-2">
            <Input value={g.title} placeholder="Group title" data-testid={`faq-group-${gi}-title`} onChange={(e) => { const arr = cloneDeep(groups); arr[gi].title = e.target.value; upd(arr); }} />
            <button onClick={() => upd(groups.filter((_, j) => j !== gi))} className="text-[#993333] px-2"><Trash2 className="w-4 h-4" /></button>
          </div>
          {(g.items || []).map((it, ii) => (
            <div key={ii} className="bg-[#FDFBF7] rounded-xl p-3 space-y-2 border border-[#F0EFEB]">
              <div className="flex justify-end"><button onClick={() => { const arr = cloneDeep(groups); arr[gi].items.splice(ii, 1); upd(arr); }} className="text-[#993333]"><Trash2 className="w-3.5 h-3.5" /></button></div>
              <Input value={it.q} placeholder="Question" data-testid={`faq-q-${gi}-${ii}`} onChange={(e) => { const arr = cloneDeep(groups); arr[gi].items[ii].q = e.target.value; upd(arr); }} />
              <Textarea rows={2} value={it.a} placeholder="Answer" data-testid={`faq-a-${gi}-${ii}`} onChange={(e) => { const arr = cloneDeep(groups); arr[gi].items[ii].a = e.target.value; upd(arr); }} />
            </div>
          ))}
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => { const arr = cloneDeep(groups); arr[gi].items = [...(arr[gi].items || []), { q: "", a: "" }]; upd(arr); }}><Plus className="w-4 h-4 mr-1" /> Add question</Button>
        </Card>
      ))}
      <Button variant="outline" className="rounded-full" onClick={() => upd([...groups, { title: "", items: [] }])}><Plus className="w-4 h-4 mr-1" /> Add group</Button>
    </div>
  );
}

/* ---------- header / footer ---------- */
function HeadFootEditor({ get, setPath }) {
  return (
    <div className="max-w-3xl space-y-5">
      <Block title="Header">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Login button text" path="header.cta_login_label" {...{ get, setPath }} />
          <Field label="Login button link" path="header.cta_login_href" {...{ get, setPath }} />
          <Field label="Primary CTA text" path="header.cta_primary_label" {...{ get, setPath }} />
          <Field label="Primary CTA link" path="header.cta_primary_href" {...{ get, setPath }} />
        </div>
        <ListEditor label="Navigation links" path="header.nav" {...{ get, setPath }} empty={{ label: "", href: "" }} fields={[{ key: "label", label: "Label" }, { key: "href", label: "Link (e.g. /about)" }]} />
      </Block>
      <Block title="Footer">
        <Area label="Tagline" path="footer.tagline" {...{ get, setPath }} />
        <Field label="Legal / copyright line" path="footer.legal" {...{ get, setPath }} />
        <FooterColumns get={get} setPath={setPath} />
      </Block>
    </div>
  );
}

function FooterColumns({ get, setPath }) {
  const cols = get("footer.columns", []) || [];
  const upd = (arr) => setPath("footer.columns", arr);
  return (
    <div className="space-y-3">
      <p className="text-sm font-semibold text-[#2C3531]">Footer columns</p>
      {cols.map((c, ci) => (
        <Card key={ci} className="p-4 border-[#E8E6E1] shadow-none bg-[#FDFBF7] space-y-2">
          <div className="flex gap-2">
            <Input value={c.title} placeholder="Column title" data-testid={`footer-col-${ci}-title`} onChange={(e) => { const arr = cloneDeep(cols); arr[ci].title = e.target.value; upd(arr); }} />
            <button onClick={() => upd(cols.filter((_, j) => j !== ci))} className="text-[#993333] px-2"><Trash2 className="w-4 h-4" /></button>
          </div>
          {(c.links || []).map((l, li) => (
            <div key={li} className="flex gap-2">
              <Input value={l.label} placeholder="Label" onChange={(e) => { const arr = cloneDeep(cols); arr[ci].links[li].label = e.target.value; upd(arr); }} />
              <Input value={l.href} placeholder="/link" onChange={(e) => { const arr = cloneDeep(cols); arr[ci].links[li].href = e.target.value; upd(arr); }} />
              <button onClick={() => { const arr = cloneDeep(cols); arr[ci].links.splice(li, 1); upd(arr); }} className="text-[#993333] px-2"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => { const arr = cloneDeep(cols); arr[ci].links = [...(arr[ci].links || []), { label: "", href: "" }]; upd(arr); }}><Plus className="w-4 h-4 mr-1" /> Add link</Button>
        </Card>
      ))}
      <Button variant="outline" className="rounded-full" onClick={() => upd([...cols, { title: "", links: [] }])}><Plus className="w-4 h-4 mr-1" /> Add column</Button>
    </div>
  );
}

/* ---------- global settings ---------- */
function SettingsEditor({ get, setPath }) {
  return (
    <div className="max-w-2xl space-y-5">
      <Block title="Brand identity">
        <Field label="Site name" path="settings.site_name" {...{ get, setPath }} testid="cms-site-name" />
        <ImageField label="Site logo" value={get("settings.logo_url")} onChange={(v) => setPath("settings.logo_url", v)} testid="cms-site-logo" />
        <div className="grid grid-cols-2 gap-3">
          <div><p className="text-sm font-medium text-[#2C3531] mb-1.5">Primary colour</p>
            <div className="flex gap-2"><input type="color" value={get("settings.brand_primary") || "#2C3531"} onChange={(e) => setPath("settings.brand_primary", e.target.value)} className="h-10 w-12 rounded" /><Input value={get("settings.brand_primary")} onChange={(e) => setPath("settings.brand_primary", e.target.value)} /></div></div>
          <div><p className="text-sm font-medium text-[#2C3531] mb-1.5">Accent colour</p>
            <div className="flex gap-2"><input type="color" value={get("settings.brand_accent") || "#9A8C73"} onChange={(e) => setPath("settings.brand_accent", e.target.value)} className="h-10 w-12 rounded" /><Input value={get("settings.brand_accent")} onChange={(e) => setPath("settings.brand_accent", e.target.value)} /></div></div>
        </div>
      </Block>
      <Block title="Contact details">
        <Field label="Contact email" path="settings.contact_email" {...{ get, setPath }} />
        <Field label="Support email" path="settings.support_email" {...{ get, setPath }} />
        <Field label="Phone" path="settings.phone" {...{ get, setPath }} />
      </Block>
      <Block title="Social links">
        <Field label="Instagram URL" path="settings.socials.instagram" {...{ get, setPath }} />
        <Field label="Facebook URL" path="settings.socials.facebook" {...{ get, setPath }} />
        <Field label="LinkedIn URL" path="settings.socials.linkedin" {...{ get, setPath }} />
        <Field label="TikTok URL" path="settings.socials.tiktok" {...{ get, setPath }} />
      </Block>
    </div>
  );
}

/* ---------- business applications (from /register) ---------- */
function Applications() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/business-applications").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const act = async (id, action) => {
    try { await api.post(`/admin/business-applications/${id}/${action}`); toast.success(`Application ${action}d`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const color = { pending: "default", approved: "secondary", rejected: "destructive" };
  return (
    <div data-testid="cms-applications" className="max-w-3xl space-y-3">
      {list.length === 0 && <p className="text-[#787672]">No applications yet.</p>}
      {list.map((a) => (
        <Card key={a.id} className="p-5 border-[#E8E6E1] shadow-none" data-testid={`application-${a.id}`}>
          <div className="flex justify-between items-start gap-3">
            <div>
              <h3 className="font-serif text-lg font-bold">{a.business_name}</h3>
              <p className="text-sm text-[#787672]">{a.contact_name} · {a.email} · {a.phone}</p>
              <p className="text-sm text-[#787672]">{a.city} · {a.specialty}</p>
              {a.message && <p className="text-sm mt-2 italic">"{a.message}"</p>}
            </div>
            <Badge variant={color[a.status] || "default"}>{a.status}</Badge>
          </div>
          {a.status === "pending" && (
            <div className="flex gap-2 mt-4">
              <Button data-testid={`approve-app-${a.id}`} onClick={() => act(a.id, "approve")} className="rounded-full bg-[#3B5341] hover:bg-[#2e4133] flex-1"><Check className="w-4 h-4 mr-1" /> Approve & create business</Button>
              <Button data-testid={`reject-app-${a.id}`} onClick={() => act(a.id, "reject")} variant="outline" className="rounded-full flex-1">Reject</Button>
            </div>
          )}
          {a.status === "approved" && a.business_slug && <p className="text-xs text-[#3B5341] mt-3">Live at /partner/{a.business_slug}</p>}
        </Card>
      ))}
    </div>
  );
}

/* ---------- contact messages ---------- */
function Messages() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/contact-submissions").then((r) => setList(r.data)); }, []);
  return (
    <div data-testid="cms-messages" className="max-w-3xl space-y-3">
      {list.length === 0 && <p className="text-[#787672]">No messages yet.</p>}
      {list.map((m) => (
        <Card key={m.id} className="p-5 border-[#E8E6E1] shadow-none" data-testid={`message-${m.id}`}>
          <div className="flex items-center gap-2 mb-1"><Mail className="w-4 h-4 text-[#9A8C73]" /><h3 className="font-semibold">{m.name}</h3></div>
          <p className="text-sm text-[#787672]">{m.email} · {m.phone}</p>
          <p className="text-sm text-[#787672]">{m.specialty} · {m.business_model}</p>
          {m.message && <p className="text-sm mt-2">{m.message}</p>}
        </Card>
      ))}
    </div>
  );
}
